import React, { InputHTMLAttributes, useState, useEffect } from 'react';
import ReactDom from 'react-dom';
import { Button, Typography } from 'lib/components';
import { InventoryTypeSelect, InventoryUnitTypeSelect } from 'lib/components/FormFields';
import VendorSelect from './VendorSelect';
import { useWindowResize } from 'hooks/useWindowResize';
import { Inventory, InventoryInput, Vendor } from 'types';
import classes from './index.module.scss';

/**
 * Props interface for the AddItemModal component
 */
interface Props {
	/** Controls whether the modal is visible */
	isOpen: boolean;
	/** Inventory item data for editing (undefined for new items) */
	inventory?: Inventory;
	/** List of vendors to populate the vendor dropdown */
	vendors: Vendor[];
	/** Callback function when modal is closed */
	onClose: () => any;
	/** Callback function when create/update action is performed */
	action: (type: 'create' | 'update', data: InventoryInput, id?: number) => void;
}

/**
 * Props interface for the TextInput component
 * Extends standard input props with additional styling options
 */
interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
	/** Label text displayed above the input */
	label: string;
	/** Optional margin styling */
	margin?: string;
}

/**
 * TextInput component
 * Renders a labeled input field with customizable styling
 *
 * @param {TextInputProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TextInput = ({ label, margin, ...props }: TextInputProps) => {
	return (
		<div className={classes['textfield-container']} style={{ margin }}>
			{/* Input label */}
			<Typography variant="h6" color="darkgrey">
				{label}
			</Typography>
			{/* Input field with spread props */}
			<input {...props} />
		</div>
	);
};

/**
 * AddItemModal component
 * Modal for creating or editing inventory items
 * Renders a form with fields for all inventory item properties
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} - Rendered component or null if not open
 */
const AddItemModal = ({ isOpen, inventory, vendors, onClose, action }: Props) => {
	// Responsive design helper
	const { isSmallerDevice } = useWindowResize();

	// Form state for inventory item properties
	const [name, setName] = useState('');
	const [sku, setSku] = useState('');
	const [stock, setStock] = useState(0);
	const [cost, setCost] = useState(0);
	const [vendorId, setVendorId] = useState<number | undefined>();
	const [typeId, setTypeId] = useState<number | undefined>();
	const [unitId, setUnitId] = useState<number | undefined>();
	const [packageQuantity, setPackageQuantity] = useState(0);
	const [liquidQuantity, setLiquidQuantity] = useState(0);

	/**
	 * Initialize form fields when inventory item changes
	 * Populates form with inventory data for editing or resets for new items
	 */
	useEffect(() => {
		if (inventory) {
			// Populate form with existing inventory data
			setName(inventory.name);
			setSku(inventory.sku);
			setStock(inventory.stock);
			setVendorId(inventory.vendorId);
			setTypeId(inventory.typeId);
			setUnitId(inventory.unitId);
			setPackageQuantity(inventory.packageQuantity);
			setLiquidQuantity(inventory.liquidQuantity ?? 0);
		} else {
			// Reset form for new inventory item
			resetFields();
		}
	}, [inventory]);

	/**
	 * Reset all form fields to default values
	 * Used when creating a new item or after form submission
	 */
	const resetFields = () => {
		setName('');
		setSku('');
		setStock(0);
		setVendorId(undefined);
		setTypeId(undefined);
		setUnitId(undefined);
		setPackageQuantity(0);
		setLiquidQuantity(0);
	};

	/**
	 * Handle form submission for create or update action
	 * Collects form data and calls the appropriate action callback
	 *
	 * @param {string} type - Action type ('create' or 'update')
	 */
	const handleAction = (type: 'create' | 'update') => {
		// Prepare inventory data from form fields
		const inventoryData: InventoryInput = {
			name,
			sku,
			stock,
			cost,
			vendorId: vendorId as number,
			typeId: typeId as number,
			unitId: unitId as number,
			packageQuantity,
			liquidQuantity,
		};

		if (type === 'create') {
			// Create new inventory item
			action('create', inventoryData);
		} else {
			// Update existing inventory item
			action('update', inventoryData, inventory?.id);
		}
		// Reset form after submission
		resetFields();
	};

	// Don't render anything if modal is not open
	if (!isOpen) {
		return null;
	}

	// Render modal using React Portal to ensure it appears above other content
	return ReactDom.createPortal(
		<div className={classes['modal']}>
			<div className={classes['inventory-modal-content']}>
				{/* Item name and vendor selection row */}
				<div className={classes['modal-form']}>
					{/* Item name input */}
					<div
						style={{
							flex: 1,
							marginRight: '1rem',
							minWidth: 300,
						}}
					>
						<TextInput
							placeholder="Name"
							label="Item Name"
							className={classes['name']}
							style={{
								width: '100%',
							}}
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					{/* Vendor dropdown */}
					<VendorSelect vendors={vendors} value={vendorId} setValue={setVendorId} />
				</div>

				{/* Item information section header */}
				<Typography
					variant={isSmallerDevice ? 'h5' : 'h6'}
					style={{
						marginTop: '2rem',
					}}
					weight="bold"
				>
					Item Information
				</Typography>

				{/* Item details row with multiple inputs */}
				<div className={classes['row']}>
					{/* Item type dropdown */}
					<InventoryTypeSelect value={typeId} setValue={setTypeId} style={{ margin: '1rem 1.5rem 0 0' }} />

					{/* Unit type dropdown */}
					<InventoryUnitTypeSelect
						value={unitId}
						setValue={setUnitId}
						style={{ margin: '1rem 1.5rem 0 0' }}
					/>

					{/* SKU input */}
					<TextInput
						margin="1rem 0 0 0"
						label="SKU"
						placeholder="0000000"
						style={{ width: '7rem', minWidth: 110, marginRight: '1.5rem' }}
						value={sku}
						onChange={(e) => setSku(e.target.value)}
					/>

					{/* Stock quantity input */}
					<TextInput
						type="number"
						margin="1rem 0 0 0"
						label="Stock"
						placeholder="000"
						style={{ width: '5rem', minWidth: 44, marginRight: '1.5rem' }}
						value={stock}
						onChange={(e) => setStock(e.target.valueAsNumber)}
					/>

					{/* Package cost input */}
					<TextInput
						type="number"
						margin="1rem 0 0 0"
						label="Package Cost"
						placeholder="Enter Package Cost"
						style={{ width: '7rem', minWidth: 120, marginRight: '1.5rem' }}
						value={cost}
						onChange={(e) => setCost(e.target.valueAsNumber)}
					/>

					{/* Package quantity input */}
					<TextInput
						type="number"
						margin="1rem 0 0 0"
						label="Package Quantity"
						placeholder="100"
						style={{ width: '6rem', minWidth: 90, marginRight: '1.5rem' }}
						value={packageQuantity}
						onChange={(e) => setPackageQuantity(e.target.valueAsNumber)}
					/>

					{/* Liquid quantity input - only shown for liquid type items (typeId === 2) */}
					{typeId === 2 && (
						<TextInput
							type="number"
							margin="1rem 0 0 0"
							label="Liquid Quantity"
							placeholder=""
							style={{ width: '6rem', minWidth: 90 }}
							value={liquidQuantity}
							onChange={(e) => setLiquidQuantity(e.target.valueAsNumber)}
						/>
					)}
				</div>

				{/* Action buttons row */}
				<div className={classes['row']}>
					{/* Submit button - text changes based on create/update mode */}
					<Button
						variant="standard"
						color="primary"
						title={inventory ? 'Update Item' : 'Add New Item'}
						className={classes['button']}
						style={{
							marginRight: '1rem',
						}}
						onClick={() => handleAction(inventory ? 'update' : 'create')}
					/>

					{/* Cancel button */}
					<Button
						variant="standard"
						color="gray"
						sizing="small"
						title="Cancel"
						onClick={onClose}
						className={classes['button']}
					/>
				</div>
			</div>
		</div>,
		document.getElementById('portal') as HTMLElement
	);
};

export default AddItemModal;
